// crewbit-admin/src/components/money/DisputeListRow.tsx
//
// Tier 2-UI Phase E (in-app bundle) — admin parity for mobile DisputeListRow.
// Web idiom: inline CSS + button element. Mirrors mobile body shape; chrome
// divergence only.

import { ChevronRight } from 'lucide-react';
import { palette, radii, spacing, typography, transition } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import type { DisputeEscalation, DisputeStatus } from '../../types/disputes';
import DisputeStatusPill from './DisputeStatusPill';

interface DisputeListRowProps {
  dispute: DisputeEscalation;
  onTap: (dispute: DisputeEscalation) => void;
  statusLabels: Record<DisputeStatus, string>;
  invoiceMissingLabel: string;
  clientUnknownLabel: string;
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1mo ago' : `${months}mo ago`;
}

export default function DisputeListRow({
  dispute,
  onTap,
  statusLabels,
  invoiceMissingLabel,
  clientUnknownLabel,
}: DisputeListRowProps) {
  const amountDollars = dispute.amount_cents != null ? dispute.amount_cents / 100 : null;
  const invoiceLabel = dispute.invoice_number
    ? `Invoice ${dispute.invoice_number}`
    : invoiceMissingLabel;
  const clientLabel = dispute.client_name ?? clientUnknownLabel;

  return (
    <button
      type="button"
      onClick={() => onTap(dispute)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        width: '100%',
        textAlign: 'left',
        padding: spacing.md,
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: radii.md,
        marginBottom: spacing.sm,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: `background ${transition.fast}, border-color ${transition.fast}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: typography.size.subheading,
              fontWeight: 600,
              color: palette.g800,
              flex: '0 1 auto',
            }}
          >
            {invoiceLabel}
          </span>
          <DisputeStatusPill status={dispute.status} label={statusLabels[dispute.status]} />
        </div>
        <div style={{ fontSize: typography.size.caption, color: palette.g600 }}>
          {clientLabel}
        </div>
        {dispute.reason ? (
          <div
            style={{
              fontSize: typography.size.caption,
              color: palette.g700,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {dispute.reason}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            marginTop: 2,
            fontSize: typography.size.caption,
            color: palette.g500,
          }}
        >
          {amountDollars != null ? (
            <span
              style={{
                color: palette.g800,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(amountDollars)}
            </span>
          ) : null}
          {amountDollars != null ? <span style={{ color: palette.g400 }}>·</span> : null}
          <span>{formatRelativeDate(dispute.created_at)}</span>
        </div>
      </div>
      <ChevronRight size={18} color={palette.g500} />
    </button>
  );
}
